import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CookieBanner from "@/components/CookieBanner";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Terms from "@/pages/Terms";
import ThankYou from "@/pages/ThankYou";
import NotFound from "@/pages/NotFound";

function LangLayout({ children }) {
  const { lang } = useParams();
  const location = useLocation();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    if (SUPPORTED_LANGS.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    document.documentElement.lang = lang || DEFAULT_LANG;
    document.title = t("meta.title");
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", t("meta.description"));
  }, [lang, i18n, t, location.pathname]);

  if (!SUPPORTED_LANGS.includes(lang)) {
    return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--anv-bg)]" data-testid="lang-layout">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <CookieBanner />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />
        <Route path="/thank_you" element={<ThankYou />} />
        <Route
          path="/:lang"
          element={
            <LangLayout>
              <Home />
            </LangLayout>
          }
        />
        <Route
          path="/:lang/privacy"
          element={
            <LangLayout>
              <PrivacyPolicy />
            </LangLayout>
          }
        />
        <Route
          path="/:lang/cookie-policy"
          element={
            <LangLayout>
              <CookiePolicy />
            </LangLayout>
          }
        />
        <Route
          path="/:lang/terms"
          element={
            <LangLayout>
              <Terms />
            </LangLayout>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
